import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../util/axiosBaseQuery";
import { CompleteStripePaymentData, InitializePaymentData, CompleteStripePaymentResponse, InitializeNoPaymentData, InitializeStripePaymentData, InitializeSubscriptionPaymentData, InitializeVoucherStripePaymentData, InitiateCustomerAgreementVerificationPayload, InitiateCustomerAgreementVerificationResponse } from "../types";
import { Order } from "../../types/order";

/**
 * The recipient as the payment routes take it — or null for none. It used to
 * be an object of nulls whenever there was no recipient, which the no-payment
 * order write (`if (recipientData)`) stored as a blank recipient contact.
 */
const recipientBody = (recipient: InitializePaymentData["recipient"]) =>
  recipient
    ? {
        firstname: recipient.firstName || null,
        lastname: recipient.lastName || null,
        email: recipient.email || null,
        telephone: recipient.phone || null,
      }
    : null;

export const paymentsApiSlice = createApi({
  reducerPath: "paymentsApiSlice",
  baseQuery: axiosBaseQuery(),
  // Only "subscriptionPayment" is both provided (getSubscriptionByCustomerAgreementId)
  // and invalidated; the payment init/execute flows redirect to Stripe / return
  // pages and have no query in this slice to refresh.
  tagTypes: ["subscriptionPayment"],
  endpoints: (builder) => ({
    initializeStripePayment: builder.mutation({
      query: (payload: InitializeStripePaymentData) => {
        return {
          url: `/payment/reservations/stripe`,
          method: "POST",
          data: {
            reservationIds: payload.reservationIds, // Optional for multi reservations payment. Used for vonovia
            contact: {
              firstname: payload.contact.firstName,
              lastname: payload.contact.lastName,
              email: payload.contact.email,
              telephone: payload.contact.phone,
              company: payload.contact.company || null,
              address: payload.contact.address || null,
              zipCode: payload.contact.zipCode || null,
              city: payload.contact.city || null,
              country: payload.contact.country || null,
            },
            recipient: recipientBody(payload.recipient),
            returnUrl: payload.returnUrl,
            cancelUrl: payload.cancelUrl,
            service: payload.service,
          },
        };
      },
    }),
    initializeVoucherStripePayment: builder.mutation({
      query: (payload: InitializeVoucherStripePaymentData) => {
        return {
          url: `/payment/vouchers/stripe`,
          method: "POST",
          data: {
            contact: {
              firstName: payload.firstName,
              lastName: payload.lastName,
              email: payload.email,
            },
            amount: payload.voucherAmount,
            returnUrl: payload.returnUrl,
            cancelUrl: payload.cancelUrl,
          },
        };
      },
    }),
    completeStripePayment: builder.mutation<CompleteStripePaymentResponse, CompleteStripePaymentData>({
      query: (payload) => {
        return {
          url: `/payments/reservations/stripe/execute`,
          method: "POST",
          data: {
            reservationIds: payload.reservationIds,
          },
        };
      },
    }),
    completeSubscriptionPayment: builder.mutation({
      query: (payload: { sessionId: string }) => {
        return {
          url: `/payments/customer-agreement/stripe/execute`,
          method: "POST",
          data: {
            sessionId: payload.sessionId,
          },
        };
      },
      // Executing the payment changes the customer agreement / subscription
      // state read by getSubscriptionByCustomerAgreementId.
      invalidatesTags: ["subscriptionPayment"],
    }),
    completeVoucherStripePayment: builder.mutation({
      query: (payload: { sessionId: string }) => {
        return {
          url: `/payments/vouchers/stripe/execute`,
          method: "POST",
          data: {
            sessionId: payload.sessionId,
          },
        };
      },
    }),

    initializeNoPayment: builder.mutation({
      query: (payload: InitializeNoPaymentData) => {
        return {
          url: `/payment/reservations/no_payment`,
          method: "POST",
          data: {
            reservationIds: payload.reservationIds, // Optional for multi reservations payment. Used for vonovia
            contact: {
              firstname: payload.contact.firstName,
              lastname: payload.contact.lastName,
              email: payload.contact.email,
              telephone: payload.contact.phone,
              company: payload.contact.company || null,
              address: payload.contact.address || null,
              zipCode: payload.contact.zipCode || null,
              city: payload.contact.city || null,
              country: payload.contact.country || null,
            },
            recipient: recipientBody(payload.recipient),
            returnUrl: payload.returnUrl,
            service: payload.service,
          },
        };
      },
    }),

    completeNoPayment: builder.mutation<Order[], CompleteStripePaymentData>({
      query: (payload) => {
        return {
          url: `/order/reservations/${payload.reservationIds.join(",")}`,
          method: "GET",
        };
      },
    }),
    initializeSubscriptionPayment: builder.mutation({
      query: (payload: InitializeSubscriptionPaymentData) => {
        return {
          url: `/payment/customer-agreement`,
          method: "POST",
          data: payload,
        };
      },
      invalidatesTags: ["subscriptionPayment"],
    }),
    initiateCustomerAgreementVerification: builder.mutation<
      InitiateCustomerAgreementVerificationResponse,
      InitiateCustomerAgreementVerificationPayload
    >({
      query: (payload) => {
        return {
          url: `/customer-agreement/verify/initiate`,
          method: "POST",
          data: payload,
        };
      },
      invalidatesTags: ["subscriptionPayment"],
    }),
    getSubscriptionByCustomerAgreementId: builder.query({
      query: (customerAgreementId: string) => {
        return {
          url: `/customer-agreement/${customerAgreementId}`,
          method: "GET",
        };
      },
      providesTags: ["subscriptionPayment"],
    }),
  }),
});

export const {
  useInitializeStripePaymentMutation,
  useCompleteStripePaymentMutation,
  useCompleteVoucherStripePaymentMutation,
  useInitializeNoPaymentMutation,
  useCompleteNoPaymentMutation,
  useInitializeVoucherStripePaymentMutation,
  useInitializeSubscriptionPaymentMutation,
  useCompleteSubscriptionPaymentMutation,
  useInitiateCustomerAgreementVerificationMutation,
  useGetSubscriptionByCustomerAgreementIdQuery,
} = paymentsApiSlice;
